/**
 * Clients Page - Multi-tenant Client Management
 *
 * Features:
 * - List all clients with filtering (tier, status)
 * - View client details (companies, entitlements)
 * - Create new clients
 * - Edit client information
 * - Soft delete (set status to 'cancelled')
 *
 * API Integration: Backend deployed at https://zp9fececl7.execute-api.us-east-2.amazonaws.com/dev
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 * Styling: Flowbite React components for modern, clean UI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Modal, Label, TextInput, Textarea, Select, Alert, Spinner } from 'flowbite-react';
import {
  listClients,
  getClient,
  createClient as apiCreateClient,
  updateClient as apiUpdateClient,
  deleteClient as apiDeleteClient,
  type Client,
  type ClientDetails,
  type CreateClientRequest,
  type UpdateClientRequest,
  type Company,
} from '../api/clients';

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    Client_ID: '',
    Client_Name: '',
    Client_Type: 'PRO' as 'OSS' | 'PRO' | 'ENTERPRISE',
    Billing_Contact_Email: '',
    Contract_Start_Date: '',
    Contract_End_Date: '',
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await listClients({
        status: statusFilter || undefined,
        tier: tierFilter || undefined,
      });

      if (response.success) {
        setClients(response.data.Records);
        console.log('[ClientsPage] Fetched clients:', {
          count: response.data.Records.length,
          total: response.meta.Total_Records,
        });
      } else {
        throw new Error(response.message || 'Failed to fetch clients');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch clients from API';
      setError(errorMessage);
      console.error('[ClientsPage] Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const fetchClientDetails = async (clientId: string) => {
    try {
      setError(null);

      const response = await getClient(clientId);

      if (response.success && response.data.Records.length > 0) {
        setSelectedClient(response.data.Records[0]);
        setShowDetailsModal(true);
        console.log('[ClientsPage] Fetched client details:', {
          clientId,
          companiesCount: response.data.Records[0].companies?.length || 0,
        });
      } else {
        throw new Error(response.message || 'Client not found');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch client details';
      setError(errorMessage);
      console.error('[ClientsPage] Error fetching client details:', err);
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);

      const createRequest: CreateClientRequest = {
        Client_ID: formData.Client_ID,
        Client_Name: formData.Client_Name,
        Client_Type: formData.Client_Type,
        Billing_Contact_Email: formData.Billing_Contact_Email || undefined,
        Contract_Start_Date: formData.Contract_Start_Date || undefined,
        Contract_End_Date: formData.Contract_End_Date || undefined,
      };

      const response = await apiCreateClient(createRequest);

      if (response.success) {
        console.log('[ClientsPage] Created client:', response.data.Records[0]);
        setShowCreateModal(false);
        resetForm();
        fetchClients();
      } else {
        throw new Error(response.message || 'Failed to create client');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create client';
      setError(errorMessage);
      console.error('[ClientsPage] Error creating client:', err);
    }
  };

  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClient) return;

    try {
      setError(null);

      const updateRequest: UpdateClientRequest = {
        Client_Name: formData.Client_Name,
        Client_Type: formData.Client_Type,
        Billing_Contact_Email: formData.Billing_Contact_Email || undefined,
        Contract_Start_Date: formData.Contract_Start_Date || undefined,
        Contract_End_Date: formData.Contract_End_Date || undefined,
      };

      const response = await apiUpdateClient(selectedClient.Client_ID, updateRequest);

      if (response.success) {
        console.log('[ClientsPage] Updated client:', response.data.Records[0]);
        setShowEditModal(false);
        resetForm();
        fetchClients();
      } else {
        throw new Error(response.message || 'Failed to update client');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update client';
      setError(errorMessage);
      console.error('[ClientsPage] Error updating client:', err);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this client? This will set the status to cancelled.')) return;

    try {
      setError(null);

      const response = await apiDeleteClient(clientId);

      if (response.success) {
        console.log('[ClientsPage] Deleted client:', clientId);
        fetchClients();
      } else {
        throw new Error(response.message || 'Failed to delete client');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete client';
      setError(errorMessage);
      console.error('[ClientsPage] Error deleting client:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      Client_ID: '',
      Client_Name: '',
      Client_Type: 'PRO',
      Billing_Contact_Email: '',
      Contract_Start_Date: '',
      Contract_End_Date: '',
    });
  };

  const openEditModal = (client: Client) => {
    setSelectedClient(client as ClientDetails);
    setFormData({
      Client_ID: client.Client_ID,
      Client_Name: client.Client_Name,
      Client_Type: client.Client_Type,
      Billing_Contact_Email: client.Billing_Contact_Email || '',
      Contract_Start_Date: client.Contract_Start_Date || '',
      Contract_End_Date: client.Contract_End_Date || '',
    });
    setShowEditModal(true);
  };

  const getTierBadgeColor = (tier: string): 'gray' | 'info' | 'purple' => {
    switch (tier) {
      case 'OSS':
        return 'gray';
      case 'PRO':
        return 'info';
      case 'ENTERPRISE':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getStatusBadgeColor = (status: string): 'success' | 'warning' | 'failure' | 'gray' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'cancelled':
        return 'failure';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
        <Button onClick={() => setShowCreateModal(true)}>
          Create Client
        </Button>
      </div>

      {error && (
        <Alert color="failure" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="mb-4 flex gap-4">
        <div>
          <Label htmlFor="status-filter">Status</Label>
          <Select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </div>

        <div>
          <Label htmlFor="tier-filter">Tier</Label>
          <Select
            id="tier-filter"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="OSS">OSS</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </Select>
        </div>
      </div>

      {/* Clients Table - Using native table for reliability */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client ID</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Companies</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No clients found
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.Client_ID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-600">
                    {client.Client_ID}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {client.Client_Name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge color={getTierBadgeColor(client.Client_Type)}>
                      {client.Client_Type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge color={getStatusBadgeColor(client.Client_Status || 'active')}>
                      {client.Client_Status || 'active'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">{client.company_count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">{client.user_count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => fetchClientDetails(client.Client_ID)}
                      >
                        View
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEditModal(client)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => handleDeleteClient(client.Client_ID)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Client Modal */}
      <Modal show={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }}>
        <Modal.Header>Create New Client</Modal.Header>
        <Modal.Body>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div>
              <Label htmlFor="create-id">Client ID *</Label>
              <TextInput
                id="create-id"
                type="text"
                required
                placeholder="e.g., ACME_CORP"
                value={formData.Client_ID}
                onChange={(e) => setFormData({ ...formData, Client_ID: e.target.value })}
              />
              <p className="text-sm text-gray-500 mt-1">Unique identifier for the client</p>
            </div>

            <div>
              <Label htmlFor="create-name">Client Name *</Label>
              <TextInput
                id="create-name"
                type="text"
                required
                placeholder="e.g., Acme Corporation"
                value={formData.Client_Name}
                onChange={(e) => setFormData({ ...formData, Client_Name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="create-tier">Tier</Label>
              <Select
                id="create-tier"
                value={formData.Client_Type}
                onChange={(e) => setFormData({ ...formData, Client_Type: e.target.value as 'OSS' | 'PRO' | 'ENTERPRISE' })}
              >
                <option value="OSS">OSS</option>
                <option value="PRO">PRO</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
              </Select>
            </div>

            <div>
              <Label htmlFor="create-billing-email">Billing Contact Email</Label>
              <TextInput
                id="create-billing-email"
                type="email"
                placeholder="billing@example.com"
                value={formData.Billing_Contact_Email}
                onChange={(e) => setFormData({ ...formData, Billing_Contact_Email: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="create-contract-start">Contract Start Date</Label>
              <TextInput
                id="create-contract-start"
                type="date"
                value={formData.Contract_Start_Date}
                onChange={(e) => setFormData({ ...formData, Contract_Start_Date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="create-contract-end">Contract End Date</Label>
              <TextInput
                id="create-contract-end"
                type="date"
                value={formData.Contract_End_Date}
                onChange={(e) => setFormData({ ...formData, Contract_End_Date: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                color="gray"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Create</Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Edit Client Modal */}
      <Modal show={showEditModal} onClose={() => { setShowEditModal(false); resetForm(); }}>
        <Modal.Header>Edit Client</Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUpdateClient} className="space-y-4">
            <div>
              <Label htmlFor="edit-id">Client ID</Label>
              <TextInput
                id="edit-id"
                type="text"
                disabled
                value={formData.Client_ID}
                className="bg-gray-100"
              />
              <p className="text-sm text-gray-500 mt-1">Client ID cannot be changed</p>
            </div>

            <div>
              <Label htmlFor="edit-name">Client Name *</Label>
              <TextInput
                id="edit-name"
                type="text"
                required
                value={formData.Client_Name}
                onChange={(e) => setFormData({ ...formData, Client_Name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-tier">Tier</Label>
              <Select
                id="edit-tier"
                value={formData.Client_Type}
                onChange={(e) => setFormData({ ...formData, Client_Type: e.target.value as 'OSS' | 'PRO' | 'ENTERPRISE' })}
              >
                <option value="OSS">OSS</option>
                <option value="PRO">PRO</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-billing-email">Billing Contact Email</Label>
              <TextInput
                id="edit-billing-email"
                type="email"
                value={formData.Billing_Contact_Email}
                onChange={(e) => setFormData({ ...formData, Billing_Contact_Email: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-contract-start">Contract Start Date</Label>
              <TextInput
                id="edit-contract-start"
                type="date"
                value={formData.Contract_Start_Date}
                onChange={(e) => setFormData({ ...formData, Contract_Start_Date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-contract-end">Contract End Date</Label>
              <TextInput
                id="edit-contract-end"
                type="date"
                value={formData.Contract_End_Date}
                onChange={(e) => setFormData({ ...formData, Contract_End_Date: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                color="gray"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Client Details Modal */}
      <Modal
        show={showDetailsModal && !!selectedClient}
        onClose={() => setShowDetailsModal(false)}
        size="3xl"
      >
        <Modal.Header>Client Details</Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Client ID</dt>
                    <dd className="text-sm font-mono font-medium text-gray-900">{selectedClient.Client_ID}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Name</dt>
                    <dd className="text-sm font-medium text-gray-900">{selectedClient.Client_Name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Tier</dt>
                    <dd>
                      <Badge color={getTierBadgeColor(selectedClient.Client_Type)}>
                        {selectedClient.Client_Type}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Status</dt>
                    <dd>
                      <Badge color={getStatusBadgeColor(selectedClient.Client_Status || 'active')}>
                        {selectedClient.Client_Status || 'active'}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Billing Contact</dt>
                    <dd className="text-sm font-medium text-gray-900">{selectedClient.Billing_Contact_Email || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Billing Level</dt>
                    <dd className="text-sm font-medium text-gray-900">{selectedClient.Billing_Level || '-'}</dd>
                  </div>
                  {selectedClient.Contract_Start_Date && (
                    <div>
                      <dt className="text-sm text-gray-500">Contract Start</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {new Date(selectedClient.Contract_Start_Date).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {selectedClient.Contract_End_Date && (
                    <div>
                      <dt className="text-sm text-gray-500">Contract End</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {new Date(selectedClient.Contract_End_Date).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Companies */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Companies ({selectedClient.companies?.length || 0})
                </h4>
                {selectedClient.companies && selectedClient.companies.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                    {selectedClient.companies.map((company) => (
                      <li key={company.Company_ID} className="p-3">
                        <div className="flex justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{company.Company_Name}</div>
                            <div className="text-sm text-gray-500 font-mono">
                              {company.Company_ID}
                            </div>
                          </div>
                          <Badge color={getStatusBadgeColor(company.Company_Status)}>
                            {company.Company_Status}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No companies assigned</p>
                )}
              </div>

              {/* Entitlements */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Entitlements ({selectedClient.entitlements?.length || 0})
                </h4>
                {selectedClient.entitlements && selectedClient.entitlements.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                    {selectedClient.entitlements.map((ent, idx) => (
                      <li key={idx} className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{ent.feature_name}</div>
                            <div className="text-sm text-gray-500">
                              {ent.usage_limit !== null
                                ? `${ent.usage_current} / ${ent.usage_limit} (${ent.reset_period})`
                                : 'Unlimited'}
                            </div>
                          </div>
                          <Badge color={ent.feature_enabled ? 'success' : 'gray'}>
                            {ent.feature_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No entitlements configured</p>
                )}
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <Button color="gray" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ClientsPage;
